import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import EpisodeProgress from '@/components/EpisodeProgress'
import YouTubePlayer from '@/components/YouTubePlayer'
import { HtmlContent } from '@/components/ui/html-content'
import { escapeHtml } from '@/utils/sanitize'
import { useEpisode, useTrackWorkbookAction, type WorkbookAction } from '@/hooks'
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Home,
  FileText,
  SkipBack,
  SkipForward,
  RotateCcw,
} from 'lucide-react'

const Episode = () => {
  const { moduleId, episodeId } = useParams()
  const modId = parseInt(moduleId || '1')
  const epId = parseInt(episodeId || '1')
  const [videoKey, setVideoKey] = useState(0)
  const [shouldRestart, setShouldRestart] = useState(false)

  // ✅ Busca dados do banco via API
  const { data, isLoading, error } = useEpisode(modId, epId)

  // Hook para rastrear ações de workbook
  const trackWorkbookMutation = useTrackWorkbookAction()

  // Função para rastrear visualização/download do workbook
  const handleTrackWorkbook = (action: WorkbookAction, workbookUrl: string) => {
    if (!data?.episode?.id) return

    trackWorkbookMutation.mutate({
      episodeId: data.episode.id,
      action,
      workbookUrl,
    })
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando episódio...</p>
        </div>
      </div>
    )
  }

  if (error || !data) {
    // Verifica se é erro de autenticação ou matrícula
    const errorMessage = error instanceof Error ? error.message : ''
    const isAuthError = errorMessage.includes('logado') || errorMessage.includes('401')
    const isEnrollmentError = errorMessage.includes('matriculado') || errorMessage.includes('403')

    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container py-16">
          <div className="max-w-md mx-auto text-center">
            {isAuthError ? (
              <>
                <div className="w-16 h-16 bg-orange-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-orange-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                </div>
                <h1 className="text-2xl font-bold mb-2">Login Necessário</h1>
                <p className="text-muted-foreground mb-6">
                  Você precisa estar logado para acessar este episódio.
                </p>
                <div className="flex gap-3 justify-center">
                  <Link to="/auth" className="btn-primary">
                    Fazer Login
                  </Link>
                  <Link to="/" className="btn-secondary">
                    Voltar ao Início
                  </Link>
                </div>
              </>
            ) : isEnrollmentError ? (
              <>
                <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-red-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <h1 className="text-2xl font-bold mb-2">Matrícula Necessária</h1>
                <p className="text-muted-foreground mb-6">
                  Você precisa se matricular neste curso para acessar os episódios.
                </p>
                <div className="flex gap-3 justify-center">
                  <Link to="/cursos" className="btn-primary">
                    Ver Cursos
                  </Link>
                  <Link to="/" className="btn-secondary">
                    Voltar ao Início
                  </Link>
                </div>
              </>
            ) : (
              <>
                <p className="text-muted-foreground mb-4">Episódio não encontrado</p>
                <Link to="/" className="btn-primary">
                  Voltar ao Início
                </Link>
              </>
            )}
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  const { episode, navigation, progress } = data
  const { module } = episode
  // Masterclass = vídeo único; esconde stepper, breadcrumb "Episódio N",
  // badge "Módulo N" e prefixo numérico no título.
  const isMasterclass = module.courseType === 'masterclass'
  // Aula "em breve" (coming_soon): teaser não-consumível. Detecção canônica é
  // pelo status; mantém o fallback de vídeo ausente por segurança.
  const isComingSoon = episode.status === 'coming_soon' || !episode.videoUrl

  // ✅ URLs de navegação dinâmicas do backend
  const getPreviousLink = () => {
    if (navigation.prevEpisode) {
      if (navigation.prevHasQuiz) {
        return `/quiz/${navigation.prevEpisode.moduleId}/${navigation.prevEpisode.number}`
      }
      return `/episode/${navigation.prevEpisode.moduleId}/${navigation.prevEpisode.number}`
    }
    return '#'
  }

  const getNextLink = () => {
    if (!navigation.hasNext) {
      // Último episódio: se tem quiz vai pro quiz, senão direto pro feedback
      return data.hasQuiz ? `/quiz/${modId}/${epId}` : `/course-feedback/${episode.module.courseId}`
    }
    // Se tem quiz publicado, vai pro quiz; senão pula direto pro próximo episódio
    if (data.hasQuiz) {
      return `/quiz/${modId}/${epId}`
    }
    return `/episode/${navigation.nextEpisode!.moduleId}/${navigation.nextEpisode!.number}`
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="container py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
          <Link to="/" className="hover:text-foreground transition-colors flex items-center gap-1">
            <Home className="w-4 h-4" />
            Início
          </Link>
          <ChevronRight className="w-4 h-4" />
          <Link to="/#modulos" className="hover:text-foreground transition-colors">
            Módulos
          </Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-primary">{module.title}</span>
          {!isMasterclass && (
            <>
              <ChevronRight className="w-4 h-4" />
              <span className="text-foreground">Episódio {epId}</span>
            </>
          )}
        </nav>

        {/* Episode Progress (stepper) — só pra cursos multi-episódio.
            Masterclass é vídeo único, stepper polui. Quando tem só 1
            episódio no módulo (mesmo em curso regular), também esconde. */}
        {!isMasterclass && (module.totalEpisodes ?? 0) > 1 && (
          <EpisodeProgress
            moduleId={modId}
            currentEpisode={epId}
            totalEpisodes={module.totalEpisodes}
          />
        )}

        {/* Episode Header */}
        <div className="mb-8 mt-6">
          {!isMasterclass && (
            <span className="module-badge mb-3 inline-block">Módulo {module.order}</span>
          )}
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground">
            {isMasterclass ? episode.title : `Episódio ${module.order}.${epId} - ${episode.title}`}
          </h1>
        </div>

        {/* Video Player */}
        <div className="mb-8">
          <div className="aspect-video rounded-xl overflow-hidden bg-card border border-border">
            {episode.videoUrl && !isComingSoon ? (
              <YouTubePlayer
                key={`${episode.id}-${videoKey}`}
                videoUrl={episode.videoUrl}
                episodeId={episode.id}
                initialProgress={
                  shouldRestart ? 0 : progress?.completed ? 0 : progress?.watchedDuration || 0
                }
                onProgressUpdate={(time, completed) => {
                  console.log(`Progresso: ${Math.floor(time)}s - Completo: ${completed}`)
                  // Reseta o flag após o vídeo começar
                  if (shouldRestart && time > 0) {
                    setShouldRestart(false)
                  }
                }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-center">
                <div>
                  <FileText className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <p className="text-xl font-semibold text-foreground mb-2">Em Breve</p>
                  <p className="text-muted-foreground">Este episódio estará disponível em breve.</p>
                </div>
              </div>
            )}
          </div>

          {/* Episode Controls */}
          <div className="flex items-center justify-center gap-4 mt-4">
            {navigation.hasPrevious ? (
              <Link
                to={getPreviousLink()}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors"
              >
                <SkipBack className="w-4 h-4" />
                <span className="hidden sm:inline">Anterior</span>
              </Link>
            ) : (
              <div className="w-[100px] sm:w-[120px]" />
            )}

            {episode.videoUrl && !isComingSoon && (
              <button
                onClick={() => {
                  setShouldRestart(true)
                  setVideoKey((prev) => prev + 1)
                }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                <span className="hidden sm:inline">Repetir</span>
              </button>
            )}

            {/* Numa aula "em breve" não há como avançar — o botão Próximo some
                (evita atravessar/contar como concluída aula não lançada). */}
            {!navigation.hasNext && !isComingSoon && (
              <Link
                to={getNextLink()}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors"
              >
                <span className="hidden sm:inline">Próximo</span>
                <SkipForward className="w-4 h-4" />
              </Link>
            )}

            {navigation.hasNext && !isComingSoon ? (
              <Link
                to={getNextLink()}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors"
              >
                <span className="hidden sm:inline">Próximo</span>
                <SkipForward className="w-4 h-4" />
              </Link>
            ) : (
              <div className="w-[100px] sm:w-[120px]" />
            )}
          </div>
        </div>

        {/* Content Section */}
        <div className="grid lg:grid-cols-3 gap-8 mb-12">
          {/* Description */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-foreground mb-4">Sobre este episódio</h2>
              {episode.description ? (
                <HtmlContent content={episode.description} className="text-muted-foreground" />
              ) : (
                <p className="text-muted-foreground leading-relaxed">
                  Neste episódio, você vai aprender sobre {episode.title.toLowerCase()}. Vamos
                  explorar conceitos fundamentais, exemplos práticos e dicas para aplicar esse
                  conhecimento no seu dia a dia. Prepare-se para transformar a maneira como você
                  trabalha com Inteligência Artificial.
                </p>
              )}
              <div className="mt-4 text-muted-foreground">
                <p className="text-sm text-muted-foreground/70 mb-1">Módulo</p>
                {module.description ? (
                  // O título não vem do editor — é campo de texto puro —, então
                  // é escapado antes de virar HTML. A descrição segue sendo
                  // sanitizada dentro do HtmlContent.
                  <HtmlContent
                    content={`<p><strong>${escapeHtml(module.title)}</strong><br/>${module.description}</p>`}
                    className="text-muted-foreground"
                  />
                ) : (
                  <p className="leading-relaxed">
                    Este conteúdo faz parte do módulo "{module.title}"
                  </p>
                )}
              </div>
            </div>

            {/* Key Points */}
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-3">
                O que você vai aprender:
              </h3>
              <ul className="space-y-2 text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  Conceitos fundamentais e aplicações práticas
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  Exemplos reais de implementação
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  Dicas e melhores práticas
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-1">•</span>
                  Exercícios para consolidar o aprendizado
                </li>
              </ul>
            </div>
          </div>

          {/* Workbook Sidebar */}
          {episode.workbookUrl && (
            <div className="lg:col-span-1">
              <div className="bg-card border border-border rounded-xl p-6 sticky top-24">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg gradient-primary flex items-center justify-center">
                    <FileText className="w-5 h-5 text-primary-foreground" />
                  </div>
                  <h3 className="font-semibold text-foreground">Workbook</h3>
                </div>

                {/* Preview/Ícone do arquivo */}
                <div className="aspect-video bg-secondary rounded-lg mb-4 flex flex-col items-center justify-center border border-border overflow-hidden">
                  {episode.workbookCoverUrl ? (
                    <img
                      src={episode.workbookCoverUrl}
                      alt="Capa do workbook"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="flex flex-col items-center justify-center p-4 gap-3">
                      <FileText className="w-16 h-16 text-primary opacity-80" />
                      <div className="text-center">
                        <p className="text-sm font-medium text-primary mb-1">
                          {episode.workbookUrl.toLowerCase().endsWith('.pdf')
                            ? 'PDF'
                            : episode.workbookUrl.toLowerCase().match(/\.(docx?)$/i)
                              ? 'DOC'
                              : episode.workbookUrl.toLowerCase().match(/\.(xlsx?)$/i)
                                ? 'XLS'
                                : episode.workbookUrl.toLowerCase().match(/\.(pptx?)$/i)
                                  ? 'PPT'
                                  : 'Arquivo'}
                        </p>
                        <p className="text-xs text-muted-foreground">Material complementar</p>
                      </div>
                    </div>
                  )}
                </div>

                <p className="text-sm text-muted-foreground mb-4">
                  Material complementar para acompanhar a aula e fazer anotações.
                </p>

                <div className="space-y-2">
                  {episode.workbookUrl.toLowerCase().endsWith('.pdf') && (
                    <a
                      href={episode.workbookUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => handleTrackWorkbook('view', episode.workbookUrl!)}
                      className="w-full btn-secondary flex items-center justify-center gap-2"
                    >
                      <FileText className="w-4 h-4" />
                      Visualizar PDF
                    </a>
                  )}
                  <a
                    href={episode.workbookUrl}
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleTrackWorkbook('download', episode.workbookUrl!)}
                    className="w-full btn-primary flex items-center justify-center gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Baixar Workbook
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        {/* Temporariamente desabilitado
        <div className="flex items-center justify-between border-t border-border pt-8">
          {navigation.hasPrevious ? (
            <Link
              to={getPreviousLink()}
              className="btn-secondary flex items-center gap-2"
            >
              <ChevronLeft className="w-4 h-4" />
              Episódio Anterior
            </Link>
          ) : (
            <div />
          )}

          {navigation.hasNext ? (
            <Link
              to={getNextLink()}
              className="btn-primary flex items-center gap-2"
            >
              Próximo Episódio
              <ChevronRight className="w-4 h-4" />
            </Link>
          ) : (
            <Link to="/" className="btn-primary flex items-center gap-2">
              Voltar ao Início
              <Home className="w-4 h-4" />
            </Link>
          )}
        </div>
        */}
      </main>

      <Footer />
    </div>
  )
}

export default Episode
